import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, FileText, CreditCard, User, LogOut, Menu, X, HelpCircle, Library, BarChart3 } from 'lucide-react';
import Logo from './ui/Logo';
import LanguageSwitcher from './ui/LanguageSwitcher';

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const username = localStorage.getItem('username') || 'User';
  const userPicture = localStorage.getItem('picture');
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  const menuItems = [
    { name: t('nav.home'), path: '/dashboard/home', icon: Home },
    { name: t('nav.exams'), path: '/dashboard/exams', icon: FileText },
    { name: t('nav.questionBanks'), path: '/dashboard/banks', icon: Library },
    { name: t('nav.profile'), path: '/dashboard/profile', icon: User },
    { name: t('nav.billing'), path: '/dashboard/billing', icon: CreditCard },
    ...(isAdmin ? [{ name: t('nav.commandCenter'), path: '/dashboard/admin', icon: BarChart3 }] : []),
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('username');
    localStorage.removeItem('picture');
    localStorage.removeItem('userId');
    localStorage.removeItem('subscriptionStatus');
    localStorage.removeItem('isAdmin');
    window.location.href = '/';
  };

  const navContent = (
    <>
      <div className="flex flex-col items-center mb-8">
        <Logo variant="full" size="sm" dark className="mb-1" />
        <div className="flex items-center gap-2 mt-1">
          {userPicture && (
            <img src={userPicture} alt="Profile" referrerPolicy="no-referrer" className="w-8 h-8 rounded-full ring-2 ring-white/30" />
          )}
          <span className="text-sm font-medium text-white/80 truncate max-w-[140px]">{username}</span>
        </div>
      </div>

      <nav className="flex-1 space-y-1 w-full">
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition relative ${
                isActive
                  ? 'bg-white/15 text-white font-medium'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
              }`}
            >
              {isActive && (
                <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary-300 rounded-r" />
              )}
              <Icon size={18} />
              {item.name}
            </Link>
          );
        })}
      </nav>

      <div className="mt-auto space-y-1">
        <a
          href="/faq"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-white/60 hover:bg-white/10 hover:text-white rounded-lg transition"
        >
          <HelpCircle size={18} />
          {t('nav.helpAndFaq')}
        </a>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-white/60 hover:bg-red-500/20 hover:text-red-300 rounded-lg transition"
        >
          <LogOut size={18} />
          {t('nav.logout')}
        </button>
        <div className="px-4 py-2">
          <LanguageSwitcher />
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-md"
      >
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 md:hidden" onClick={() => setMobileOpen(false)}>
          <div
            className="w-64 h-full bg-gradient-to-b from-primary-900 via-primary-800 to-primary-900 p-6 flex flex-col animate-slide-right"
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={() => setMobileOpen(false)}
              className="self-end mb-4 text-white/50 hover:text-white"
            >
              <X size={20} />
            </button>
            {navContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex w-64 bg-gradient-to-b from-primary-900 via-primary-800 to-primary-900 shadow-lg p-6 flex-col h-screen sticky top-0">
        {navContent}
      </div>
    </>
  );
};

export default Sidebar;
